import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../scss/components/Orders.scss';
import '../scss/components/Repeater.scss';
import OrderItem from '../components/Orders/OrderItem.tsx';
import NavigateConfirmModal from '../components/Nav/NavigateConfirmModal.tsx';
import Button from '../components/Form/Button.tsx';
import FileDropzone, { UploadedFile } from '../components/Form/FileDropzone.tsx';
import { useOrdersApi } from '../features/auth/hooks/use-orders-api.ts';
import { useListsApi } from '../features/auth/hooks/use-lists-api.ts';
import { usePricesApi } from '../features/auth/hooks/use-prices-api.ts';
import { useProfileContext } from '../features/auth/hooks/use-profile-context.ts';
import { useHelper } from '../features/functions/helper.tsx';
import { orderToFormData } from '../features/functions/orderToFormData.ts';
import { getExamRegistrationStatus, IExamInfo } from '../features/functions/examRegistration.ts';
import { IOrder } from '../interface/IOrder.ts';
import { ApiResponseType } from '../features/api/types.ts';

export default function Orders() {
    const { getOrders, getExamProjects, cancelOrder, postOrderEarlyCheck, postOrderCertificate } = useOrdersApi();
    const { getList } = useListsApi();
    const { getNextExam } = usePricesApi();
    const { isRoleValidated } = useProfileContext();
    const { formatDateFromTimestamp } = useHelper();
    const navigate = useNavigate();
    const location = useLocation();

    // A notice handed over after a successful form submit (see VerificationForm).
    const initialNotice = (location.state as any)?.notice || '';
    // Deep-link target: /auftraege?order=<id> opens + scrolls to that order (e.g. from the email).
    const targetOrderId = new URLSearchParams(location.search).get('order');
    const scrolledRef = useRef(false);
    const [orders, setOrders] = useState<Array<IOrder>>([]);
    const [projects, setProjects] = useState<Array<any>>([]);
    const [educationPrograms, setEducationPrograms] = useState<Array<any>>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [responseMessage, setResponseMessage] = useState(initialNotice);
    const [responseMessageClass, setResponseMessageClass] = useState(initialNotice ? 'success show' : '');
    const [cancelTarget, setCancelTarget] = useState<IOrder|null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [earlyCheckTarget, setEarlyCheckTarget] = useState<IOrder|null>(null);
    const [uploadTarget, setUploadTarget] = useState<IOrder|null>(null);
    const [uploadFiles, setUploadFiles] = useState<Array<UploadedFile>>([]);
    const [uploadError, setUploadError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    // Next exam incl. registration_open / allowed_exam_types, as in VerificationForm.
    const [examInfo, setExamInfo] = useState<IExamInfo|null>(null);
    const [examInfoLoaded, setExamInfoLoaded] = useState(false);
    const [examInfoFailed, setExamInfoFailed] = useState(false);

    const hasEarlyCheck = (o:IOrder) => o.request_early_check === '1' || o.request_early_check === 1 || (o.request_early_check as any) === true;
    const hasCertificate = (o:IOrder) => o.educational_certificate === '1' || o.educational_certificate === 1 || (o.educational_certificate as any) === true;

    // The backend copies a late certificate onto the registration's other open orders
    // (same exam project and exam number) — mirrors OrderService::findOpenRegistrationSiblings.
    const registrationSiblings = (target:IOrder) => target.exam_number ? orders.filter(o => o.id !== target.id
        && String(o.project_id) === String(target.project_id)
        && o.exam_number === target.exam_number
        && ['received', 'invoiced', 'invoice_paid', 'approved'].includes(o.status_value as string)
        && !hasCertificate(o)) : [];

    const loadOrders = () => {
        setIsLoading(true);
        getOrders().then((data:any) => {
            // getOrders guarantees an array payload on success.
            setOrders(data.type === ApiResponseType.OK ? data.payload : []);
            setIsLoading(false);
        }).catch(() => {
            setResponseMessage('Die Aufträge konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
            setResponseMessageClass('error');
            setIsLoading(false);
        });
    };

    const loadExamInfo = () => {
        getNextExam()
            .then((res:any) => {
                setExamInfo(res && typeof res === 'object' ? res : null);
                setExamInfoFailed(false);
            })
            .catch(() => setExamInfoFailed(true))
            .finally(() => setExamInfoLoaded(true));
    };

    // Same derivation as VerificationForm: no upcoming exam, deadline passed or
    // already registered hides the registration button without replacement.
    const canRegister = getExamRegistrationStatus(examInfo, examInfoLoaded, examInfoFailed) === 'open';

    useEffect(() => {
        loadOrders();
        loadExamInfo();
        getExamProjects().then((res:any) => setProjects(Array.isArray(res) ? res : [])).catch(() => {});
        getList('education_program').then((res:any) => setEducationPrograms(Array.isArray(res) ? res : [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const projectMap = useMemo(() => {
        const map = new Map<string, any>();
        projects.forEach((p:any) => map.set(String(p.id), p));
        return map;
    }, [projects]);

    // Group orders under their exam project (run). Current run (soonest future exam
    // date) first, then past runs newest→oldest; an unknown-project bucket sorts last.
    const groups = useMemo(() => {
        const now = Math.floor(Date.now() / 1000);
        let currentId:string|null = null;
        let currentTs = Infinity;
        projects.forEach((p:any) => {
            const ts = Number(p.exam_date) || 0;
            if (ts >= now && ts < currentTs) { currentTs = ts; currentId = String(p.id); }
        });

        const buckets = new Map<string, { key:string, name:string, examDate:number, isCurrent:boolean, orders:Array<IOrder> }>();
        orders.forEach((order:IOrder) => {
            const pid = order.project_id != null && order.project_id !== '' ? String(order.project_id) : '';
            const key = pid || '__none__';
            if (!buckets.has(key)) {
                const proj = projectMap.get(pid);
                buckets.set(key, {
                    key,
                    name: proj?.name || 'Prüfungstermin',
                    examDate: Number(proj?.exam_date) || 0,
                    isCurrent: pid !== '' && pid === currentId,
                    orders: [],
                });
            }
            buckets.get(key)!.orders.push(order);
        });

        return Array.from(buckets.values()).sort((a, b) => b.examDate - a.examDate);
    }, [orders, projects, projectMap]);

    useEffect(() => {
        if (initialNotice) {
            const timer = setTimeout(() => { setResponseMessage(''); setResponseMessageClass(''); }, 6000);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRetry = (order:IOrder) => {
        const target = isRoleValidated ? '/erneute-pruefung' : '/erstpruefung';
        navigate(target, { state: { prefill: orderToFormData(order) } });
    };

    const startRegistration = () => {
        navigate(isRoleValidated ? '/erneute-pruefung' : '/erstpruefung');
    };

    const cancelNotice = (order:IOrder) => {
        const examNumber = order.exam_number || order.name || `#${order.id}`;
        const deadline = Number(projectMap.get(String(order.project_id))?.registration_deadline) || 0;
        // Unix seconds, like the deadline; the no-refund hint only applies in the last week.
        const examDate = Number(projectMap.get(String(order.project_id))?.exam_date) || 0;
        const withinLastWeek = examDate > 0 && examDate - Math.floor(Date.now() / 1000) < 7 * 24 * 60 * 60;
        const examSubject = order.subject ? ' ' + order.subject.replace('Anmeldung ', '').trim() : '';
        // The DIN exam has its own cancellation terms; one order carries one exam type.
        const isDin = order.exam_type_din_competence === '1' || order.exam_type_din_competence === (1 as any) || (order.exam_type_din_competence as any) === true;

        return (
            <div className="cancel-notice">
                <h5 className="cancel-headline">Möchten Sie Ihre Buchung{examSubject} mit der Prüfungsnummer <strong>{examNumber}</strong> wirklich stornieren?</h5>
                { isDin ? (
                    <>
                        <p><strong>Folgende Stornierungsgebühren fallen an:</strong></p>
                        <p>Bei einer Stornierung der DIN-Kompetenzprüfung fällt eine Bearbeitungsgebühr in Höhe einer Gebühreneinheit zzgl. MwSt. in gesetzlicher Höhe gemäß DIN CERTCO an.</p>
                    </>
                ) : (
                    <>
                        <p>Folgende Stornierungsgebühren fallen an:</p>
                        <ul>
                            <li>Stornierung vor Anmeldeschluss{deadline > 0 ? ` ${formatDateFromTimestamp(deadline, 'dd.mm.yyyy')}` : ''}: Bearbeitungsgebühr in Höhe von 100 EUR zzgl. MwSt.</li>
                            <li>Stornierung bis zu einer Woche vor Prüfungstermin: 50% der fälligen Prüfungsgebühren zzgl. MwSt. in gesetzlicher Höhe</li>
                            <li>Stornierung weniger als eine Woche vor Prüfungstermin: 100% der fälligen Prüfungsgebühren zzgl. MwSt. in gesetzlicher Höhe</li>
                        </ul>
                        <p>Bereits gezahlte Prüfungsgebühren werden entsprechend anteilig vergütet.</p>
                        { withinLastWeek && (
                            <p><strong>Bitte beachten Sie: Bei einer Stornierung weniger als eine Woche vor dem Prüfungstermin erfolgt keine Rückerstattung der Prüfungsgebühren.</strong></p>
                        ) }
                    </>
                ) }
            </div>
        );
    };

    const closeCancelModal = () => {
        setCancelTarget(null);
    };

    const confirmCancel = () => {
        if (!cancelTarget) {
            return;
        }
        const order = cancelTarget;
        closeCancelModal();
        setIsCancelling(true);
        setResponseMessage('');
        setResponseMessageClass('');
        cancelOrder(order.id).then((res:any) => {
            setIsCancelling(false);
            if (res.type === ApiResponseType.OK) {
                setResponseMessage('Der Auftrag wurde storniert.');
                setResponseMessageClass('success show');
                setTimeout(() => { setResponseMessage(''); setResponseMessageClass(''); }, 6000);
                loadOrders();
                loadExamInfo();
            } else {
                setResponseMessage((res.payload && res.payload.error) ? res.payload.error : 'Der Auftrag konnte nicht storniert werden. Bitte versuchen Sie es erneut.');
                setResponseMessageClass('error');
            }
        }).catch(() => {
            setIsCancelling(false);
            setResponseMessage('Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.');
            setResponseMessageClass('error');
        });
    };

    // Opt in the early check for the whole registration run (all still-open orders of
    // the clicked order's project that have not requested it yet).
    const confirmEarlyCheck = () => {
        if (!earlyCheckTarget) {
            return;
        }
        const target = earlyCheckTarget;
        setEarlyCheckTarget(null);
        setResponseMessage('');
        setResponseMessageClass('');
        const ids = orders
            .filter(o => o.status_value === 'received' && !hasEarlyCheck(o) && String(o.project_id) === String(target.project_id))
            .map(o => o.id);
        postOrderEarlyCheck(ids.length ? ids : [target.id]).then((res:any) => {
            if (res.type === ApiResponseType.OK) {
                setResponseMessage('Die sofortige Bearbeitung wurde angefordert.');
                setResponseMessageClass('success show');
                setTimeout(() => { setResponseMessage(''); setResponseMessageClass(''); }, 6000);
                loadOrders();
            } else {
                setResponseMessage((res.payload && res.payload.error) ? res.payload.error : 'Die sofortige Bearbeitung konnte nicht angefordert werden. Bitte versuchen Sie es erneut.');
                setResponseMessageClass('error');
            }
        }).catch(() => {
            setResponseMessage('Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.');
            setResponseMessageClass('error');
        });
    };

    const openUploadModal = (order:IOrder) => {
        setUploadTarget(order);
        setUploadFiles([]);
        setUploadError('');
    };

    const closeUploadModal = () => {
        if (isUploading) {
            return;
        }
        setUploadTarget(null);
        setUploadFiles([]);
        setUploadError('');
    };

    const confirmUpload = () => {
        if (!uploadTarget || isUploading) {
            return;
        }
        if (uploadFiles.length === 0) {
            setUploadError('Bitte laden Sie das Abschlusszeugnis hoch.');
            return;
        }
        setIsUploading(true);
        setUploadError('');
        postOrderCertificate(uploadTarget.id, uploadFiles).then((res:any) => {
            setIsUploading(false);
            if (res.type === ApiResponseType.OK && res.payload?.success !== false) {
                setUploadTarget(null);
                setUploadFiles([]);
                setResponseMessage('Ihr Abschlusszeugnis wurde hochgeladen.');
                setResponseMessageClass('success show');
                setTimeout(() => { setResponseMessage(''); setResponseMessageClass(''); }, 6000);
                loadOrders();
            } else {
                setUploadError((res.payload && res.payload.error) ? res.payload.error : 'Das Abschlusszeugnis konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.');
            }
        }).catch(() => {
            setIsUploading(false);
            setUploadError('Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.');
        });
    };

    // Scroll the deep-linked order into view once, after the orders have loaded.
    useEffect(() => {
        if (!targetOrderId || scrolledRef.current || isLoading || orders.length === 0) {
            return;
        }
        const el = document.getElementById('order-' + targetOrderId);
        if (el) {
            scrolledRef.current = true;
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders, isLoading, targetOrderId]);

    return (
        <div id="orders">
            <h1 className="h3 mb-2">Zentralprüfung</h1>

            { responseMessage && (
                <div className={`message${responseMessageClass ? ' ' + responseMessageClass : ''}`} role="alert">
                    <span>{responseMessage}</span>
                </div>
            ) }

            { isLoading ? (
                <div className="loader"></div>
            ) : orders.length > 0 && (
                <div className="orders-list repeater">
                    { groups.map((group) => (
                        <div className="project-group" key={group.key}>
                            <div className="project-group-header">
                                <h2 className="h5 m-0">{ group.name }</h2>
                                { group.examDate > 0 && (
                                    <span className="project-group-date">{ formatDateFromTimestamp(group.examDate, 'dd.mm.yyyy') }</span>
                                ) }
                                { group.isCurrent && <span className="project-group-badge">Aktueller Prüfungstermin</span> }
                            </div>
                            { group.orders.map((order) => (
                                <OrderItem
                                    key={String(order.id)}
                                    order={order}
                                    isCancelling={isCancelling}
                                    onCancel={(o) => setCancelTarget(o)}
                                    onRetry={handleRetry}
                                    onEarlyCheck={(o) => setEarlyCheckTarget(o)}
                                    onUploadCertificate={openUploadModal}
                                    initialExpanded={targetOrderId != null && String(order.id) === targetOrderId}
                                    educationPrograms={educationPrograms}
                                />
                            )) }
                        </div>
                    )) }
                </div>
            ) }

            { canRegister && (
                <Button buttonType="primary" onClick={startRegistration}>Zur Zentralprüfung anmelden</Button>
            ) }

            { cancelTarget && (
                <NavigateConfirmModal
                    setterCallback={closeCancelModal}
                    proceed={confirmCancel}
                    text={cancelNotice(cancelTarget)}
                    leaveText="Ja, ich will stornieren"
                    cancelText="Nein, ich will nicht stornieren"
                />
            ) }

            { uploadTarget && (
                <NavigateConfirmModal
                    setterCallback={closeUploadModal}
                    proceed={confirmUpload}
                    text={
                        <div className={`certificate-upload${isUploading ? ' loading' : ''}`}>
                            <div className="loader"></div>
                            <p className="m-0">Bitte laden Sie Ihr Abschlusszeugnis Level I eines akkreditierten Aus- und Weiterbildungsprogramms für die Prüfungsnummer <strong>{uploadTarget.exam_number || uploadTarget.name || `#${uploadTarget.id}`}</strong> hoch.</p>
                            { registrationSiblings(uploadTarget).length > 0 && (
                                <p className="m-0">Das Dokument wird allen Prüfungen dieser Anmeldung zugeordnet ({ [uploadTarget, ...registrationSiblings(uploadTarget)].map(o => o.subject || o.name).join(', ') }).</p>
                            ) }
                            <FileDropzone
                                name="certificate_upload"
                                files={uploadFiles}
                                onChange={(updated) => { setUploadFiles(updated); setUploadError(''); }}
                                maxFiles={5}
                                maxSizeKb={2048}
                                error={uploadError}
                            />
                        </div>
                    }
                    leaveText={isUploading ? 'Wird hochgeladen …' : 'Hochladen'}
                />
            ) }

            { earlyCheckTarget && (
                <NavigateConfirmModal
                    setterCallback={() => setEarlyCheckTarget(null)}
                    proceed={confirmEarlyCheck}
                    text={<p className="align-center">Ich verlange ausdrücklich, dass der FPSB Deutschland mit der Ausführung der „Anmeldung zur Zentralprüfung" vor Ablauf der Widerrufsfrist beginnt. Mir ist bekannt, dass mein Widerrufsrecht bei vollständiger Bearbeitung und Bestätigung der „Anmeldung zur Zentralprüfung" durch den FPSB Deutschland erlischt.</p>}
                    leaveText={'Sofortige Bearbeitung verlangen'}
                />
            ) }
        </div>
    )
}
